<template>
  <VContainer>
    <VCard
      max-width="1200"
      class="pa-6"
    >
      <VCardTitle class="text-h5 font-weight-bold mb-4">
        <span>Manage Test Cases by Environment</span>
      </VCardTitle>
      <VCardText>
        <v-tabs
          v-model="activeTab"
          class="mb-4"
        >
          <v-tab value="automation">All Test Cases</v-tab>
        </v-tabs>

        <v-window v-model="activeTab">
          <!-- All Test Cases Tab -->
          <v-window-item value="automation">
            <VTable>
              <thead>
                <tr>
                  <th>No</th>
                  <th>Test Case Name</th>
                  <th
                    v-for="env in envList"
                    :key="env.value"
                  >
                    {{ env.label }}
                  </th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(test, i) in sortedTestCases"
                  :key="test.name"
                >
                  <td>{{ i + 1 }}</td>
                  <td>{{ test.name }}</td>
                  <td
                    v-for="env in envList"
                    :key="env.value"
                    class="text-center"
                  >
                    <VCheckbox
                      v-model="selectedTestCases[env.value]"
                      :value="test.name"
                      hide-details
                      density="compact"
                      :disabled="isSaving"
                      @update:model-value="onCheckboxUpdate(test.name, env.value, $event)"
                    />
                  </td>
                  <td class="text-center">
                    <VBtn
                      icon="ri-delete-bin-5-line"
                      color="error"
                      size="small"
                      :disabled="isDeletingTestCase"
                      @click="openDeleteTestCaseDialog(test)"
                    >
                    </VBtn>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </v-window-item>
        </v-window>
      </VCardText>
    </VCard>
    <v-snackbar
      v-model="isShowSnackBar"
      multi-line
      :globalTop="true"
      :color="alertType"
      dismissible
      class="mb-4"
      @click:close="clearAlert"
    >
      {{ alertMessage }}
    </v-snackbar>
  </VContainer>

  <!-- Delete Automation Test Case Dialog -->
  <v-dialog
    v-model="showDeleteTestCaseDialog"
    max-width="450"
  >
    <v-card>
      <v-card-title class="headline">Delete Test Case</v-card-title>
      <v-card-text>
        Are you sure you want to delete test case <strong>"{{ testCaseToDelete?.name }}"</strong>?
        <br />
        <small class="text-error">This action cannot be undone and will remove all environment associations.</small>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey"
          @click="closeDeleteTestCaseDialog"
          :disabled="isDeletingTestCase"
          >Cancel</v-btn
        >
        <v-btn
          color="error"
          @click="deleteTestCase"
          :loading="isDeletingTestCase"
          >Delete</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { useAlert } from '@/composables/useAlert'
import { useConfigStore } from '@/stores/config'
import { computed, onMounted, ref } from 'vue'

const configStore = useConfigStore()

// Derived environment list from configStore
const envList = computed(() =>
  configStore.environments.map((env: any) => ({
    label: env.aliasName,
    value: env.name,
  })),
)

const ENVIRONMENTS = computed(() => envList.value.map((env: any) => env.value))
type EnvAlias = string

const { alertMessage, alertType, isShowSnackBar, showAlert, clearAlert } = useAlert()

interface TestCaseRow {
  testCases: { id: number; name: string; url: string }
  environments: { id: number; name: string; aliasName: string }
  enabled: boolean
}

interface TestCase {
  name: string
  htmlUrl?: string
  enabled: boolean
}

// Active tab
const activeTab = ref('automation')

// Automation state
const testCases = ref<TestCase[]>([])
const selectedTestCases = ref<Record<string, string[]>>({})
const isSaving = ref(false)
const isLoadingTestCases = ref(false)
const testCaseEnvIdMap = ref<Record<string, { testCaseId: number; environmentId: number }>>({})

// Delete automation test case state
const showDeleteTestCaseDialog = ref(false)
const isDeletingTestCase = ref(false)
const testCaseToDelete = ref<TestCase | null>(null)

// Add computed property for sorted test cases
const sortedTestCases = computed(() => {
  return [...testCases.value].sort((a, b) => a.name.localeCompare(b.name))
})

// Delete automation test case functions
function openDeleteTestCaseDialog(testCase: TestCase) {
  testCaseToDelete.value = testCase
  showDeleteTestCaseDialog.value = true
}

function closeDeleteTestCaseDialog() {
  showDeleteTestCaseDialog.value = false
  testCaseToDelete.value = null
}

async function deleteTestCase() {
  if (!testCaseToDelete.value) return

  isDeletingTestCase.value = true
  try {
    const response = (await $fetch('/api/setting/delete-test-case', {
      method: 'DELETE',
      body: { name: testCaseToDelete.value.name },
      headers: { 'Content-Type': 'application/json' },
    })) as { success: boolean; message?: string }

    if (!response.success) {
      throw new Error(response.message || 'Failed to delete test case')
    }

    closeDeleteTestCaseDialog()
    showAlert(response.message || 'Test case deleted successfully', 'success')
    await fetchTestCases()
  } catch (err: any) {
    showAlert(err.message || 'Failed to delete test case', 'error')
  } finally {
    isDeletingTestCase.value = false
    setTimeout(() => {
      clearAlert()
    }, 3000)
  }
}

// Initialize selectedTestCases based on environment list
function initializeSelectedTestCases() {
  const initialState: Record<string, string[]> = {}
  envList.value.forEach((env: any) => {
    initialState[env.value] = []
  })
  selectedTestCases.value = initialState
}

async function fetchTestCases() {
  try {
    isLoadingTestCases.value = true
    const response = await $fetch('/api/setting/test-cases-environment', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    if (response.success && 'data' in response) {
      processFetchedData(response.data as TestCaseRow[])
    } else {
      console.warn('Invalid response:', response)
      testCases.value = []
    }
  } catch (error) {
    console.error('Error fetching test cases:', error)
  } finally {
    isLoadingTestCases.value = false
  }
}

function processFetchedData(rawData: TestCaseRow[]) {
  const testCaseMap = new Map<string, TestCase>()
  const selectedMap: Record<string, string[]> = {}

  // Initialize selectedMap with environment names from config
  envList.value.forEach((env: any) => {
    selectedMap[env.value] = []
  })

  const idMap: Record<string, { testCaseId: number; environmentId: number }> = {}

  for (const row of rawData) {
    const testCaseName = row.testCases.name
    const envAlias = row.environments.name

    if (!testCaseMap.has(testCaseName)) {
      testCaseMap.set(testCaseName, {
        name: testCaseName,
        htmlUrl: row.testCases.url,
        enabled: true,
      })
    }
    idMap[`${testCaseName}|${envAlias}`] = {
      testCaseId: row.testCases.id,
      environmentId: row.environments.id,
    }
    if (row.enabled) {
      selectedMap[envAlias]?.push(testCaseName)
    }
  }

  testCases.value = Array.from(testCaseMap.values())
  selectedTestCases.value = selectedMap
  testCaseEnvIdMap.value = idMap
}

function onCheckboxUpdate(testCaseName: string, envAlias: string, event: unknown) {
  const checked = Array.isArray(event) && event.includes(testCaseName)
  updateTestCaseEnabled(testCaseName, envAlias, checked)
}

async function updateTestCaseEnabled(testCaseName: string, envAlias: string, enabled: boolean) {
  try {
    isSaving.value = true
    const ids = testCaseEnvIdMap.value[`${testCaseName}|${envAlias}`]
    if (!ids) return

    const response = await $fetch('/api/setting/test-cases-environment', {
      method: 'PATCH',
      body: {
        testCaseId: ids.testCaseId,
        environmentId: ids.environmentId,
        enabled,
      },
    })

    if (!response.success) {
      throw new Error('message' in response && response.message ? response.message : 'Failed to update test case')
    }
    showAlert('Test case updated successfully', 'success')
  } catch (error) {
    const message = error instanceof Error ? error.message : 'An unexpected error occurred.'
    showAlert(message, 'error')
  } finally {
    isSaving.value = false
  }
}

onMounted(async () => {
  // Fetch environments from config store if not already loaded
  if (configStore.environments.length === 0) {
    await configStore.fetchDataEnvironment()
  }

  // Initialize selectedTestCases after environments are loaded
  initializeSelectedTestCases()

  // Fetch test cases
  await fetchTestCases()
})
</script>

<style scoped></style>
